import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { CollaborativeType } from 'account-hybrid/features/account-settings/enums/collaborative-type.enum';
import { ExperienceLicensePermission } from 'account-hybrid/features/experiences/enums/experience-license-permission.enum';
import { signOut } from 'account-hybrid/store/actions/authentication.actions';
import * as fromRoot from 'account-hybrid/store/reducers/index';
import _ from 'lodash';
import { Subject } from 'rxjs';
import { DiscoveryPropertySettingState } from 'shared';
import { safeLocalStorageSet } from '../../../../account-shared/utils/safe-storage.util';
import { Company, Property, User } from '..';
import { LicenseType } from '../models/licenses';


// In-memory copy of the authenticated session is the source of truth; localStorage is
// best-effort persistence on top of it. Safari (especially Private Browsing) can throw
// QuotaExceededError on write — that must never crash bootstrap. The server re-injects
// `session` on every authenticated load (HomeController.V2), so auth never depends on
// this persisting. Declared above the bootstrap block below to avoid a TDZ reference.
let cachedSession: User | null;

function writeSession(value: User | null): void {
    cachedSession = value;
    safeLocalStorageSet('flipto.session', JSON.stringify(value));
}

declare const session;
// update session if we got it
if (typeof session !== 'undefined') {
    writeSession(session);
}

@Injectable({
    providedIn: 'root'
})
export class SessionService {
    licenseChanged$ = new Subject();

    signOut() {
    }

    constructor(store: Store<fromRoot.State>) {
        // hack so that we dont store Store instance on session as angularjs tries to clone these objects which is slowing everything substantially
        this.signOut = () => {
            store.dispatch(signOut());
        };
    }

    get(): User | null {
        return getSession();
    }

    set(session: User | null) {
        writeSession(session);
    }

    get properties() {
        if (!this.get()) {
            return [];
        }
        return this.get().Properties;
    }

    get collaborativeProperties() {
        if (!this.get()) {
            return [];
        }
        return this.get().CollaborativeProperties ?? [];
    }

    get companies() {
        if (!this.get()) {
            return [];
        }
        return this.get().Companies;
    }

    getCompany(uuid: string) {
        return this.companies.find(c => c.UUID.toLocaleLowerCase() === (!!uuid ? uuid.toLocaleLowerCase() : null));
    }

    getProperty(uuid: string) {
        const lower = !!uuid ? uuid.toLocaleLowerCase() : null;
        return this.properties.find(p => p.UUID.toLocaleLowerCase() === lower)
            ?? this.collaborativeProperties.find(p => p.UUID.toLocaleLowerCase() === lower);
    }

    getCompanyProperties(companyUuid: string) {
        const company = this.getCompany(companyUuid);
        return !!company ?
            // associations might contain properties not accessible to current session -> filter those out
            company.Associations.map(uuid => this.getProperty(uuid)).filter(p => !!p) :
            [];
    }

    addNewProperty(property: Property, companyUuid: string) {
        const userSession = _.cloneDeep(this.get());
        const company = userSession.Companies.find(c => c.UUID === companyUuid);
        company.Associations.push(property.UUID);
        userSession.Properties.push(property);
        clearSessionCache();
        this.set(userSession);
    }

    addNewCompany(company: Company) {
        const userSession = _.cloneDeep(this.get());
        userSession.Companies.push(company);
        clearSessionCache();
        this.set(userSession);
    }

    addCompanyPropertyAssociation(companyUuid: string, propertyUuid: string) {
        const userSession = _.cloneDeep(this.get());
        const company = userSession.Companies.find(c => c.UUID === companyUuid);
        if (!company.Associations.find(associatedPropertyUuid => associatedPropertyUuid === propertyUuid) &&
            userSession.Properties.find(p => p.UUID === propertyUuid)) {
            company.Associations.push(propertyUuid);
        }
        clearSessionCache();
        this.set(userSession);
    }

    onPasswordForcefullyChanged() {
        const userSession = _.cloneDeep(this.get());
        userSession.IsForceChangePassword = false;
        clearSessionCache();
        this.set(userSession);
    }

    onDisplayCurrencyChanged(displayCurrencyCode: string) {
        const userSession = _.cloneDeep(this.get());
        userSession.DisplayCurrencyCode = displayCurrencyCode;
        clearSessionCache();
        this.set(userSession);
    }

    onFeatureFlagChanged(companyUuid: string, featureFlag: string, value: boolean) {
        const userSession = _.cloneDeep(this.get());
        const company = userSession.Companies.find(c => c.UUID.toLocaleLowerCase() === companyUuid.toLocaleLowerCase());
        company.FeatureFlags[featureFlag] = value;
        clearSessionCache();
        this.set(userSession);
    }

    onCompanyLicenseChanged(companyUuid: string, license: LicenseType | number, isActive: DiscoveryPropertySettingState, permissions?: ExperienceLicensePermission) {
        const userSession = _.cloneDeep(this.get());
        const company = userSession.Companies.find(c => c.UUID.toLocaleLowerCase() === companyUuid.toLocaleLowerCase());
        if (isActive === DiscoveryPropertySettingState.Disabled) {
            _.remove(company.Licenses, l => l == license);
            _.remove(company?.LicensesExtended, l => l.LicenseId == license);
        } else {
            // For all non-disabled states (Published, Preview, Hidden, FliptoOnly),
            // keep the entry in LicensesExtended so the UI can read the state.
            // Only add to the simple Licenses array for Published/Preview.
            if (isActive === DiscoveryPropertySettingState.Preview || isActive === DiscoveryPropertySettingState.Published) {
                company.Licenses = _.uniq([...company.Licenses, ...[license]]);
            } else {
                _.remove(company.Licenses, l => l == license);
            }
            const newEntry = { LicenseId: license, Permissions: permissions ?? 0, IsActive: isActive };
            const filtered = (company?.LicensesExtended || []).filter(l => l.LicenseId !== license);
            company.LicensesExtended = [...filtered, newEntry];
        }
        clearSessionCache();
        this.set(userSession);
        this.licenseChanged$.next(null);
    }

    onCompanyDataGranularityChanged(companyUuid: string, dataGranularity: number) {
        const userSession = _.cloneDeep(this.get());
        const company = userSession.Companies.find(c => c.UUID.toLocaleLowerCase() === companyUuid.toLocaleLowerCase());
        company.DataGranularity = dataGranularity;
        clearSessionCache();
        this.set(userSession);
        this.licenseChanged$.next(null);
    }

    onCollaborativeTypeChanged(companyUuid: string, collaborativeType: CollaborativeType) {
        const userSession = _.cloneDeep(this.get());
        const company = userSession.Companies.find(c => c.UUID.toLocaleLowerCase() === companyUuid.toLocaleLowerCase());
        company.Settings.CollaborativeTypeId = collaborativeType;
        clearSessionCache();
        this.set(userSession);
    }

    onPropertyLicenseChanged(propertyUuid: string, license: LicenseType | number, isActive: DiscoveryPropertySettingState, permissions?: ExperienceLicensePermission) {
        const userSession = _.cloneDeep(this.get());
        const property = userSession.Properties.find(p => p.UUID.toLocaleLowerCase() === propertyUuid.toLocaleLowerCase());
        if (isActive === DiscoveryPropertySettingState.Disabled) {
            _.remove(property.Licenses, l => l == license);
            _.remove(property?.LicensesExtended, l => l.LicenseId == license);
        } else {
            if (isActive === DiscoveryPropertySettingState.Preview || isActive === DiscoveryPropertySettingState.Published) {
                property.Licenses = _.uniq([...property.Licenses, ...[license]]);
            } else {
                _.remove(property.Licenses, l => l == license);
            }
            const newEntry = { LicenseId: license, Permissions: permissions ?? 0, IsActive: isActive };
            const filtered = (property?.LicensesExtended || []).filter(l => l.LicenseId !== license);
            property.LicensesExtended = [...filtered, newEntry];
        }
        clearSessionCache();
        this.set(userSession);
        this.licenseChanged$.next(null);
    }
}


function getSessionCached() {
    if (!cachedSession) {
        try {
            const stored = localStorage.getItem('flipto.session');
            cachedSession = stored ? JSON.parse(stored) : null;
        } catch (error) {
            // Unavailable or corrupt storage — treat as no persisted session.
            console.warn(`getSessionCached: could not read flipto.session: ${error instanceof Error ? error.name : String(error)}`);
            cachedSession = null;
        }
        // localStorage is only a cache. The server injects `session` into the page on
        // every authenticated load (HomeController.V2), so fall back to it when the
        // persisted copy is missing/unreadable (e.g. Safari Private Browsing, where the
        // write silently failed). This keeps getSession() — which the whole app reads
        // through — returning the real session instead of null.
        if (!cachedSession && typeof session !== 'undefined' && session) {
            cachedSession = session;
        }
    }
    return cachedSession;
}

export function clearSessionCache() {
    cachedSession = null;
}

export function getSession(): User {
    return getSessionCached();
}

export function hasPermissionInProperty(propertyUuid, requiredPermission): boolean {
    const session = getSession();
    if (!session) {
        return false;
    }
    const property = session.Properties.find(p => p.UUID === propertyUuid);
    // tslint:disable-next-line: no-bitwise
    return property && (property.Permissions & requiredPermission) === requiredPermission;
}

export function hasPermissionInCompany(companyUuid, requiredPermission): boolean {
    const session = getSession();
    if (!session) {
        return false;
    }
    const company = session.Companies.find(c => c.UUID === companyUuid);
    // tslint:disable-next-line: no-bitwise
    return company && (company.Permissions & requiredPermission) === requiredPermission;
}

window['hasPermissionInProperty'] = hasPermissionInProperty;
